import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MessageInput from '../aicoach/MessageInput';
import TypingIndicator from '../aicoach/TypingIndicator';
import { Message } from '../aicoach/ChatInterface';
import { apiService } from '../../services/api';
import InfoTooltip from '../common/InfoTooltip';
import { fmtUsd, fmtUsdShortSigned, fmtPct, fmtShare } from '../../utils/performanceFormat';

/**
 * Hero stats for the Strategy Review header, straight from the Performance
 * page's gold payload (types/performance.ts): money in USD, returns as
 * fractions, null = unknown ("—"). stockPnl + optionPnl + dividends = totalPnl
 * and optionPnl = premiumIncome + costOfCappedUpside (computed by the backend).
 */
export interface ReviewStats {
  totalPnl: number | null;
  stockPnl: number | null;
  optionPnl: number | null;
  premiumIncome: number | null;
  costOfCappedUpside: number | null;
  dividends: number | null;
  spyReturn: number | null;          // fraction
  roaic: number | null;              // fraction
  incomeYield: number | null;        // fraction per year
  lossesCushionedShare: number | null; // fraction of closed trades
  lossesCushionedContext: number | null; // fraction of the stock's fall the premium offset
}

interface StrategyReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  isLoading: boolean;
  brief: string | null;
  error: string | null;
  cached: boolean;
  selectedPeriod: string;
  stats: ReviewStats;
  conversationId: number | null;
}

const pnlColor = (value: number | null): string => {
  if (value == null) return '#9CA3AF';
  if (value > 0) return '#22C55E';
  if (value < 0) return '#EF4444';
  return '#9CA3AF';
};


const StatCard: React.FC<{
  label: string;
  tooltip: string;
  value: string;
  valueColor?: string;
  sub?: string;
}> = ({ label, tooltip, value, valueColor = '#E5E7EB', sub }) => (
  <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '8px 10px' }}>
    <div style={{ fontSize: '9px', color: '#9CA3AF', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.2 }}>
      {label}<InfoTooltip text={tooltip} />
    </div>
    <div style={{ fontSize: '15px', fontWeight: 600, color: valueColor, lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: '9px', color: '#6B7280', marginTop: '2px', lineHeight: 1.25 }}>{sub}</div>}
  </div>
);

/**
 * Parse text containing markdown links [text](url) into React elements.
 */
const renderTextWithLinks = (text: string): React.ReactNode => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#A78BFA', textDecoration: 'underline', textUnderlineOffset: '2px' }}
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

/**
 * Render a Strategy-Review-style markdown block: ## headings become section
 * titles, paragraphs use the muted body palette, and any "Bottom Line"
 * section is rendered as the purple callout. Used for both the initial
 * review brief and follow-up assistant responses so the visual language
 * stays consistent throughout the modal.
 */
const BriefBody: React.FC<{ text: string; streaming?: boolean }> = ({ text, streaming }) => {
  if (!text && !streaming) return null;

  const sections: { title?: string; body: string; isBottomLine?: boolean }[] = [];
  const parts = text.split(/^## /m);

  const preamble = parts[0]?.trim();
  if (preamble) {
    sections.push({ body: preamble });
  }

  for (let i = 1; i < parts.length; i++) {
    const newlineIdx = parts[i].indexOf('\n');
    if (newlineIdx === -1) {
      sections.push({ title: parts[i].trim(), body: '' });
    } else {
      const title = parts[i].substring(0, newlineIdx).trim();
      const body = parts[i].substring(newlineIdx + 1).trim();
      const isBottomLine = title.toLowerCase().includes('bottom line');
      sections.push({ title, body, isBottomLine });
    }
  }

  return (
    <div style={{ color: '#D1D5DB', fontSize: '14px', lineHeight: 1.7 }}>
      {sections.map((section, idx) => {
        if (section.isBottomLine) {
          return (
            <div
              key={idx}
              style={{
                marginTop: '20px',
                padding: '14px 16px',
                backgroundColor: 'rgba(167, 139, 250, 0.08)',
                border: '1px solid rgba(167, 139, 250, 0.2)',
                borderRadius: '8px',
              }}
            >
              <div style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#A78BFA',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '6px',
              }}>
                {section.title}
              </div>
              <div style={{ color: '#E5E7EB', fontSize: '14px', lineHeight: 1.6 }}>
                {renderTextWithLinks(section.body)}
              </div>
            </div>
          );
        }

        return (
          <div key={idx} style={{ marginBottom: '18px' }}>
            {section.title && (
              <h3 style={{
                margin: '0 0 6px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#E5E7EB',
              }}>
                {section.title}
              </h3>
            )}
            {section.body.split('\n\n').map((para, pIdx) => (
              <p key={pIdx} style={{ margin: '0 0 8px', color: '#9CA3AF', fontSize: '13px', lineHeight: 1.7 }}>
                {renderTextWithLinks(para)}
              </p>
            ))}
          </div>
        );
      })}
      {streaming && (
        <span style={{
          display: 'inline-block',
          width: '2px',
          height: '14px',
          backgroundColor: '#A78BFA',
          marginLeft: '2px',
          animation: 'blink 1s step-end infinite',
          verticalAlign: 'text-bottom',
        }} />
      )}
    </div>
  );
};

/**
 * User question rendered in the same palette as the brief — a small uppercase
 * "You asked" label above the question text, separated by a thin purple
 * accent line on the left.
 */
const UserQuestionBlock: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      borderLeft: '2px solid rgba(167, 139, 250, 0.4)',
      paddingLeft: '12px',
      marginBottom: '14px',
    }}
  >
    <div style={{
      fontSize: '11px',
      fontWeight: 700,
      color: '#A78BFA',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginBottom: '4px',
    }}>
      You asked
    </div>
    <div style={{ color: '#E5E7EB', fontSize: '14px', lineHeight: 1.6 }}>
      {renderTextWithLinks(text)}
    </div>
  </div>
);

const StrategyReviewModal: React.FC<StrategyReviewModalProps> = ({
  isOpen,
  onClose,
  onRefresh,
  isLoading,
  brief,
  error,
  cached,
  selectedPeriod,
  stats,
  conversationId,
}) => {
  const navigate = useNavigate();
  const [followUps, setFollowUps] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatStreaming, setChatStreaming] = useState(false);
  const followUpsEndRef = useRef<HTMLDivElement>(null);

  // Reset follow-ups whenever a fresh review is generated (different conversation_id)
  useEffect(() => {
    setFollowUps([]);
    setChatInput('');
    setChatStreaming(false);
  }, [conversationId]);

  // Auto-scroll the follow-up thread as new messages stream in
  useEffect(() => {
    followUpsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [followUps, chatStreaming]);

  if (!isOpen) return null;

  const reviewReady = !!brief && !isLoading && !!conversationId;

  const handleSendFollowUp = async (text: string) => {
    if (!text.trim() || !conversationId || chatStreaming) return;

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    const assistantId = `a-${Date.now() + 1}`;
    const assistantStub: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };

    setFollowUps(prev => [...prev, userMsg, assistantStub]);
    setChatInput('');
    setChatStreaming(true);

    let receivedAny = false;
    let errored = false;

    const appendChunk = (chunk: string) => {
      if (!chunk) return;
      receivedAny = true;
      setFollowUps(prev =>
        prev.map(m => (m.id === assistantId ? { ...m, content: m.content + chunk } : m))
      );
    };

    try {
      await apiService.streamAICoachChat(
        text,
        appendChunk,
        () => {
          setChatStreaming(false);
          if (!receivedAny && !errored) {
            setFollowUps(prev =>
              prev.map(m =>
                m.id === assistantId
                  ? { ...m, content: 'Sorry — I had nothing to say there. Try rephrasing?' }
                  : m
              )
            );
          }
        },
        (errMsg: string) => {
          errored = true;
          console.error('Strategy review follow-up error:', errMsg);
          setChatStreaming(false);
          setFollowUps(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, content: 'Sorry — I hit an error answering that. Please try again.' }
                : m
            )
          );
        },
        conversationId,
      );
    } catch (e) {
      console.error('Strategy review follow-up failed:', e);
      setChatStreaming(false);
    }
  };

  const handleContinueInCoach = () => {
    if (!conversationId) return;
    onClose();
    navigate('/ai-coach', { state: { conversationId } });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          border: '1px solid #374151',
          maxWidth: '760px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #374151',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#A78BFA"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
              <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
              <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
            </svg>
            <h2 style={{ margin: 0, color: '#E5E7EB', fontSize: '18px', fontWeight: 600 }}>
              Strategy Review
            </h2>
            <span style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#A78BFA',
              backgroundColor: 'rgba(167, 139, 250, 0.15)',
              padding: '2px 8px',
              borderRadius: '4px',
              letterSpacing: '0.5px',
            }}>
              {selectedPeriod}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={onRefresh}
              disabled={isLoading}
              title="Refresh analysis"
              style={{
                background: 'none',
                border: 'none',
                color: isLoading ? '#4B5563' : '#9CA3AF',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                padding: '4px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.color = '#A78BFA'; }}
              onMouseLeave={(e) => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.color = '#9CA3AF'; }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  animation: isLoading ? 'spin 1s linear infinite' : 'none',
                }}
              >
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
              </svg>
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#9CA3AF',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '4px',
                lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Stats — compact, income-forward (mirrors the performance-page hero):
            Total result, the option income engine, return efficiency, downside
            protection. Income-forward avoids leading with the red net option P&L. */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid #374151',
          flexShrink: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '8px',
        }}>
          <StatCard
            label="Total P&L"
            tooltip="Your total profit or loss for the period (stock + options + dividends)."
            value={fmtUsd(stats.totalPnl)}
            valueColor={pnlColor(stats.totalPnl)}
            sub={stats.spyReturn != null ? `vs S&P ${fmtPct(stats.spyReturn, 1)}` : `${selectedPeriod} period`}
          />
          <StatCard
            label="Covered Call Income"
            tooltip={`Premium income — the time value you keep as your calls decay (the strategy's purpose). The cost of capped upside is the gain handed back when stocks rise past their strikes; the two net to Option P&L.\n\nPremium income + cost of capped upside = net Option P&L.`}
            value={fmtUsd(stats.premiumIncome)}
            valueColor={pnlColor(stats.premiumIncome)}
            sub={`cap ${fmtUsdShortSigned(stats.costOfCappedUpside)} · net ${fmtUsdShortSigned(stats.optionPnl)}`}
          />
          <StatCard
            label="Return on Capital"
            tooltip="Total P&L (incl. dividends) over the period divided by the average capital committed. Not annualised under a year."
            value={fmtPct(stats.roaic, 1)}
            valueColor={pnlColor(stats.roaic)}
            sub={stats.incomeYield != null ? `income ${fmtPct(stats.incomeYield, 1)}/yr` : 'ROAIC'}
          />
          <StatCard
            label="Losses Cushioned"
            tooltip={`Share of closed trades that lost money, where the premium softened the stock's fall${stats.lossesCushionedContext != null ? ` — the premium offset ${fmtShare(stats.lossesCushionedContext)} of those stock losses` : ''}.`}
            value={fmtShare(stats.lossesCushionedShare)}
            sub="of closed trades"
          />
        </div>

        {/* Scrollable body: review + follow-ups. Inner wrapper keeps text at a
            readable measure even though the modal is wide enough for the KPI row. */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            @keyframes blink { 50% { opacity: 0; } }
          `}</style>

          {isLoading && !brief && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  border: '3px solid #374151',
                  borderTopColor: '#7C3AED',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px',
                }}
              />
              <p style={{ color: '#9CA3AF', fontSize: '14px', margin: 0 }}>
                Analyzing your strategy...
              </p>
            </div>
          )}

          {error && !isLoading && (
            <div
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                color: '#FCA5A5',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          {brief && <BriefBody text={brief} streaming={isLoading} />}

          {/* Follow-up Q&A thread (only after the review is fully streamed and persisted) */}
          {reviewReady && (
            <div style={{ marginTop: '24px', borderTop: '1px solid #374151', paddingTop: '20px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#9CA3AF',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '12px',
              }}>
                Ask a follow-up
              </div>

              {followUps.length === 0 && (
                <p style={{ color: '#6B7280', fontSize: '13px', margin: '0 0 8px' }}>
                  Want to dig into a specific number, ticker, or section above? Ask away — I have the full review in mind.
                </p>
              )}

              {followUps.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  {followUps.map((m) => {
                    if (m.role === 'user') {
                      return <UserQuestionBlock key={m.id} text={m.content} />;
                    }
                    const isLastAssistant =
                      followUps[followUps.length - 1]?.id === m.id;
                    const isStreamingThis = chatStreaming && isLastAssistant;
                    if (!m.content && isStreamingThis) {
                      return (
                        <div key={m.id} style={{ marginBottom: '18px' }}>
                          <TypingIndicator />
                        </div>
                      );
                    }
                    return (
                      <div key={m.id} style={{ marginBottom: '18px' }}>
                        <BriefBody text={m.content} streaming={isStreamingThis} />
                      </div>
                    );
                  })}
                  <div ref={followUpsEndRef} />
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        {/* Footer: input + handoff (only after the review is ready) */}
        {reviewReady && (
          <div style={{
            borderTop: '1px solid #374151',
            padding: '12px 24px 16px',
            flexShrink: 0,
          }}>
            <div style={{ maxWidth: '820px', margin: '0 auto' }}>
            <MessageInput
              value={chatInput}
              onChange={setChatInput}
              onSend={handleSendFollowUp}
              disabled={chatStreaming}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button
                onClick={handleContinueInCoach}
                style={{
                  background: 'none',
                  border: '1px solid rgba(167, 139, 250, 0.4)',
                  color: '#A78BFA',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(167, 139, 250, 0.1)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'none';
                }}
              >
                Continue in AI Coach
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyReviewModal;
